import { useForm } from 'react-hook-form';
import { React, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { login } from '../../actions/auth';
import { CloseEyeIcon, OpenEyeIcon } from '../../components/icons/IconProvider';
import { Alert } from '../../services/notiflix.service';
import { AuthService, ConfigService } from '../../services';
import ButtonLoader from '../../components/ButtonLoader';
import TwoStepVerificationOtp from '../../components/auth/TwoStepVerificationOtp';
import AuthHeaderTextComponent from '../../components/auth/AuthHeaderTextComponent';
import FormInput from '../../components/FormInput';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwordShown, setPasswordShown] = useState(false);
  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const [authUser, setAuthUser] = useState({});
  const [isLoader, setLoader] = useState(false);
  const schema = Yup.object({
    email: Yup.string().required('Email is required').email('Email is invalid'),
    password: Yup.string().required('Password is required'),
  }).required();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onLogin = async (_input) => {
    try {
      setLoader(true);
      const loginData = {
        email: _input.email,
        password: _input.password,
      };

      let response = await AuthService.login(loginData);
      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }
      if (response.data.respCode === 401) {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }

      let authUser = {
        userId: response.data.id,
        email: response.data.email,
        profilePicture: response.data.profile_picture,
        role: response.data.role,
        token: response.data.token,
        userName: response.data.full_name,
        email_verified_at: response.data.email_verified_at,
        is2FAEnabled: response.data.is_two_factor_enabled,
        isPasswordChange: response.data.is_change_password,
      };
      setAuthUser(authUser);
      let _is2FAEnabled = response.data.is_two_factor_enabled;
      if (response.data.is_user && _is2FAEnabled === 1) {
        setLoader(false);
        setShowAuthenticator(true);
        return false;
      } else {
        await Promise.resolve(dispatch(login(authUser)));
        setLoader(false);
        navigate('/dashboard');
      }
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
      return false;
    }
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  };
  return (
    <div className="hasBGLogin">
      <div className="flex items-center justify-center min-h-screen">
        {showAuthenticator === false && (
          <div className="w-full md:w-96 bg-white px-6 py-10 rounded-md m-6">
            <AuthHeaderTextComponent
              title={`Welcome to ${ConfigService.appName()}`}
              desc={'Login using your registered credentials'}
            />
            <form onSubmit={handleSubmit(onLogin)}>
              <FormInput
                label="Email Address"
                name="email"
                type="email"
                placeholder="Email Address"
                register={register}
                errors={errors}
                className="mb-2"
              />
              <div className="relative">
                <FormInput
                  label={'Password'}
                  name="password"
                  type={passwordShown ? 'text' : 'password'}
                  placeholder="Password"
                  register={register}
                  errors={errors}
                  className="mb-2 "
                />
                <Link to="" onClick={() => togglePasswordVisiblity()}>
                  <div className="absolute right-0 top-8 w-10 h-10 flex justify-center items-center">
                    {passwordShown ? <OpenEyeIcon /> : <CloseEyeIcon />}
                  </div>
                </Link>
              </div>

              <div>
                <Link
                  to="/forgot-password"
                  className={`text-[#3D4CB2] text-sm ${
                    isLoader ? 'pointer-events-none text-gray-400' : ''
                  }`}
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="mt-4">
                <button
                  disabled={isLoader}
                  type="submit"
                  className={`w-full bg-GreenColor ${
                    isLoader ? 'relative' : ''
                  }`}
                >
                  {isLoader ? (
                    <>
                      <span className="absolute inset-0 flex items-center justify-center">
                        <ButtonLoader title="Processing" />
                      </span>
                      <span className="invisible">Login</span>
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        {showAuthenticator && <TwoStepVerificationOtp authUser={authUser} />}
      </div>
    </div>
  );
};

export default Login;
